import { useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";

interface FilterChipsProps {
  selectedFilters: string[];
  onFilterChange: (filters: string[]) => void;
  sortBy: string;
  onSortChange: (sort: string) => void;
}

const filterOptions = [
  { id: "filhote", label: "Aceita filhote" },
  { id: "idoso", label: "Aceita idoso" },
  { id: "sem-outros-pets", label: "Sem outros pets" },
  { id: "quintal", label: "Com quintal" },
  { id: "cameras", label: "Com câmeras" },
  { id: "24h", label: "24h com o pet" },
  { id: "medicacao", label: "Medicação" },
];

const sortOptions = [
  { id: "recommended", label: "Recomendados" },
  { id: "price-low", label: "Menor preço" },
  { id: "rating", label: "Melhor avaliados" },
  { id: "distance", label: "Mais próximos" },
];

export function FilterChips({ selectedFilters, onFilterChange, sortBy, onSortChange }: FilterChipsProps) {
  const [priceRange, setPriceRange] = useState([50, 150]);

  const toggleFilter = (filterId: string) => {
    if (selectedFilters.includes(filterId)) {
      onFilterChange(selectedFilters.filter((f) => f !== filterId));
    } else {
      onFilterChange([...selectedFilters, filterId]);
    }
  };

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide py-3 px-4 md:px-0">
        {/* More Filters Button */}
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full flex items-center gap-2 flex-shrink-0 border-2"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filtros
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Filtros</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div>
                <h4 className="font-semibold mb-3">Faixa de preço (por noite)</h4>
                <div className="px-2">
                  <Slider
                    value={priceRange}
                    onValueChange={setPriceRange}
                    max={300}
                    min={20}
                    step={10}
                    className="mb-2"
                  />
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>R$ {priceRange[0]}</span>
                    <span>R$ {priceRange[1]}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Características</h4>
                <div className="flex flex-wrap gap-2">
                  {filterOptions.map((filter) => (
                    <button
                      key={filter.id}
                      onClick={() => toggleFilter(filter.id)}
                      className={cn(
                        "px-4 py-2 rounded-full text-sm font-medium transition-all border",
                        selectedFilters.includes(filter.id)
                          ? "bg-forest text-white border-forest"
                          : "bg-background border-border hover:border-foreground"
                      )}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Ordenar por</h4>
                <div className="flex flex-wrap gap-2">
                  {sortOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => onSortChange(option.id)}
                      className={cn(
                        "px-4 py-2 rounded-full text-sm font-medium transition-all border",
                        sortBy === option.id
                          ? "bg-forest text-white border-forest"
                          : "bg-background border-border hover:border-foreground"
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Quick Filter Chips */}
        {filterOptions.slice(0, 6).map((filter) => (
          <button
            key={filter.id}
            onClick={() => toggleFilter(filter.id)}
            className={cn(
              "px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap border flex-shrink-0",
              selectedFilters.includes(filter.id)
                ? "bg-forest text-white border-forest"
                : "bg-background border-border hover:border-foreground"
            )}
          >
            {filter.label}
            {selectedFilters.includes(filter.id) && (
              <X className="w-3 h-3 ml-1.5 inline" />
            )}
          </button>
        ))}

        {/* Sort Dropdown styled as chip */}
        <select
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value)}
          className="px-4 py-2 rounded-full text-sm font-medium border border-border bg-background appearance-none cursor-pointer hover:border-foreground transition-all flex-shrink-0"
        >
          {sortOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Active Filters Summary */}
      {selectedFilters.length > 0 && (
        <div className="flex items-center gap-2 px-4 md:px-0 pb-3">
          <span className="text-sm text-muted-foreground">
            {selectedFilters.length} filtro{selectedFilters.length > 1 ? "s" : ""} ativo{selectedFilters.length > 1 ? "s" : ""}
          </span>
          <button
            onClick={() => onFilterChange([])}
            className="text-sm text-coral font-semibold hover:underline"
          >
            Limpar todos
          </button>
        </div>
      )}
    </div>
  );
}
